import { useState } from 'react'
import { MdCallSplit, MdDeveloperMode, MdGavel, MdGridView, MdOutlineArchive } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { IAppSummary } from '~/types/application'
import type { IEngineTab } from '~/types/arbitration'

import ABTestEngine from './ABTestEngine'
import ArchiveEngine from './ArchiveEngine'
import EnginePageSelector from './components/Tabs'
import DevelopmentEngine from './DevEngine'
import ReviewEngine from './ReviewEngine'
import VersionEngine from './VersionEngine'

import { EnvironmentBadge } from '~/components/Badges'
import Header from '~/components/Header/Classic'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAppSummary } from '~/providers/AppSummary'
import { useAppSummaryDetailsQueryV2 } from '~/queries/applications'
import { ENGINE_TYPE } from '~/types/arbitration'
import { transformByDeviceType } from '~/utils/application'

import './styles.scss'

const tabs: IEngineTab[] = [
  {
    engineType: ENGINE_TYPE.DEVELOPMENT,
    name: (
      <>
        <MdDeveloperMode />
        Development
        <EnvironmentBadge value="dev" />
      </>
    ),
  },
  {
    engineType: ENGINE_TYPE.VERSION,
    name: (
      <>
        <MdGridView size={14} />
        Version
        <EnvironmentBadge value="dev" />
      </>
    ),
  },
  {
    engineType: ENGINE_TYPE.REVIEW,
    name: (
      <>
        <MdGavel />
        Review
        <EnvironmentBadge value="prod" />
      </>
    ),
  },
  {
    engineType: ENGINE_TYPE.ARCHIVE,
    name: (
      <>
        <MdOutlineArchive size={16} />
        Archive
        <EnvironmentBadge value="prod" />
      </>
    ),
  },
  {
    engineType: ENGINE_TYPE.AB_TEST,
    name: (
      <>
        <MdCallSplit />
        AB testing
        <EnvironmentBadge value="prod" />
      </>
    ),
  },
]

const Arbitration = (): ReactElement => {
  /** Local state */

  const { appDetails, appKey, deviceType } = useAppSummary()

  const [activeTab, setActiveTab] = useState(tabs[0].engineType)

  useDocumentTitle('DS Arbitrations')

  const getEngineComponent = (selectedApp: IAppSummary, engineType: ENGINE_TYPE) => {
    const engines = {
      [ENGINE_TYPE.DEVELOPMENT]: <DevelopmentEngine selectedApp={selectedApp} />,
      [ENGINE_TYPE.REVIEW]: <ReviewEngine selectedApp={selectedApp} />,
      [ENGINE_TYPE.ARCHIVE]: <ArchiveEngine selectedApp={selectedApp} />,
      [ENGINE_TYPE.VERSION]: <VersionEngine selectedApp={selectedApp} />,
      [ENGINE_TYPE.AB_TEST]: <ABTestEngine selectedApp={selectedApp} />,
    }

    return engines[engineType]
  }

  /** Queries & Mutations */

  const { data: appSummary } = useAppSummaryDetailsQueryV2(appKey, deviceType)

  /** Render */

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  return (
    <div className="arbitration-view">
      <Header apps={appSelectorApp ? [appSelectorApp] : []} isSwitchDisabled={isSwitchDisabled} />
      <EnginePageSelector activeTab={activeTab} onTabClick={(engineType) => setActiveTab(engineType)} tabs={tabs} />
      {appSummary ? <>{getEngineComponent(appSummary, activeTab)}</> : null}
    </div>
  )
}

export default Arbitration
